import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeriodicReportDialog from 'app/components/Dialog/PeriodicReportDialog';
import { getOpenFileFromFullPath, getUserFullName, highlightText, sortByProperty } from 'app/utils/appHelpers';
import { useSelector } from 'react-redux';
import { useTemplateFontSettings } from 'styles/templateFontSettings';
import PeriodicReportDataTableToolbar from './PeriodicReportDataTableToolbar';

function PeriodicReportDataTable({ mainData = [], column, pageTitle }) {
    const [data, setData] = useState(mainData);
    const [data2, setData2] = useState([]);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [fileDialogStatus, setFileDialogStatus] = useState(false);
    const [highlightQuery, setHighlightQuery] = useState('');
    const [dialogSelectedUnit, setDialogSelectedUnit] = useState(null);
    const [fileList, setFileList] = useState([]);

    const { data: unitList, isLoading: unitLoading } = useSelector(state => state.unit);
    const { data: userList, isLoading: userLoading } = useSelector(state => state.user);

    const { baseFontSize, lineHeight, iconFontSize } = useTemplateFontSettings();

    useEffect(() => {
        let data = mainData;
        data.forEach(item => {
            const unit = unitList.find(r => r.id === item.unitId);
            if (unit) {
                item.unit = unit;
            }
            const user = userList.find(r => r.id === item.userId);
            if (user) {
                item.user = user;
            }
        });
        setData2(data)
    }, [mainData])

    const groupedData = useMemo(() => {

        let updatedData = data;
        updatedData = sortByProperty(updatedData, "year", 'desc')
        updatedData = sortByProperty(updatedData, "pdate", 'desc')
        const map = new Map();
        updatedData.forEach(item => {
            const key = `${item.unitId}_${item.year}`;
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key).push(item);
        });
        return Array.from(map.entries());
    }, [data]);

    const addAfterTable = (item) => {
        setData(prev => [item, ...prev]);
        setData2(prev => [item, ...prev]);
    };

    const updateAfterTable = (item) => {

        let tmp = [...data];
        const index = tmp.findIndex(x => x.id === item.id);
        if (item.deleted) {
            if (index !== -1) {
                tmp.splice(index, 1);
            }
        } else {
            index !== -1 ? tmp[index] = item : tmp.push({ ...item, id: Number(tmp[tmp.length - 1]?.id || 0) + 1 });
        }
        setData([...tmp]);
        setData2([...tmp]);
    };

    const closeFileDialog = () => {
        setFileDialogStatus(false);
    };

    const handleFileButtonClick = (item) => {
        setDialogSelectedUnit(item);
        let temp = [...data];
        temp = temp.filter(x => Number(x.unitId) === Number(item.unitId) && Number(x.year) === Number(item.year))
        temp.forEach(x => {
            const user = userList.find(u => u.id === Number(x.userId));
            x.user = user;
        })
        temp = sortByProperty(temp, "pdate", 'desc')
        setFileList(temp);
        setFileDialogStatus(true);
    };



    return (
        (unitLoading && userLoading) ? <CircularProgress /> :

            <Paper className="col-md-12" sx={{ minWidth: 0, overflow: 'hidden' }}>
                <PeriodicReportDataTableToolbar
                    pageTitle={pageTitle}
                    data2={data2}
                    setData={setData}
                    setFileDialogStatus={() => {
                        setDialogSelectedUnit(null);
                        setFileList([]);
                        setFileDialogStatus(true);
                    }}
                    setHighlightQuery={setHighlightQuery}
                />

                <div className="flex-auto" style={{ minWidth: 0 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table
                            size="small"
                            sx={{
                                minWidth: 960,
                                '& .MuiTableCell-head': { whiteSpace: 'normal' },
                                '& .MuiTableSortLabel-root': { whiteSpace: 'normal' },
                                '& .MuiTableSortLabel-icon': { flexShrink: 0 },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {column.map(col => (
                                        <TableCell
                                            key={col.id}
                                            align={col.align}
                                            padding={col.disablePadding ? 'none' : 'normal'}
                                            sx={{ whiteSpace: 'normal' }}
                                        >
                                            <Tooltip
                                                title="Sırala"
                                                placement={col.align === 'right' ? 'bottom-end' : 'bottom-start'}
                                                enterDelay={300}
                                            >
                                                <TableSortLabel
                                                    active={orderBy === col.id}
                                                    direction={order}
                                                    sx={{ whiteSpace: 'normal', '& .MuiTableSortLabel-icon': { flexShrink: 0 } }}
                                                    onClick={(e) => {
                                                        const isAsc = orderBy === col.id && order === 'asc';
                                                        setOrder(isAsc ? 'desc' : 'asc');
                                                        setOrderBy(col.id);
                                                    }}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </Tooltip>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(([key, reports]) => {
                                    const [unitIdStr, yearStr] = key.split('_');
                                    const unitId = Number(unitIdStr);
                                    const year = Number(yearStr);
                                    const period1Report = reports.find(r => r.period === "1.Dönem" && r.filePath);
                                    const period2Report = reports.find(r => r.period === "2.Dönem" && r.filePath);
                                    const item = reports[0];

                                    const user = userList.find(u => u.id === Number(item.userId));
                                    const unit = unitList.find(u => u.id === unitId);
                                    return (
                                        <TableRow hover key={key}>
                                            <TableCell align="left" padding="normal" style={{ fontSize: baseFontSize, lineHeight: lineHeight }}>
                                                {highlightText(unit.inst, highlightQuery)}
                                            </TableCell>
                                            <TableCell align="left" padding="none" style={{ fontSize: baseFontSize, lineHeight: lineHeight }}>
                                                {highlightText(unit ? unit.name : '', highlightQuery)}
                                            </TableCell>
                                            <TableCell align="left" padding="none" style={{ fontSize: baseFontSize, lineHeight: lineHeight }}>
                                                {highlightText(getUserFullName(user), highlightQuery)}
                                            </TableCell>
                                            <TableCell align="left" padding="none" style={{ fontSize: baseFontSize, lineHeight: lineHeight }}>
                                                {highlightText(`${year}`, highlightQuery)}
                                            </TableCell>
                                            <TableCell align="center" padding="none">
                                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                                    <Tooltip title={period1Report ? "1.Dönem Raporu Aç" : "1.Dönem Raporu Yüklenmemiş"}>
                                                        <span>
                                                            <Button
                                                                style={{ minWidth: '30px', fontSize: iconFontSize }}
                                                                onClick={() => period1Report &&

                                                                    getOpenFileFromFullPath(period1Report.filePath)

                                                                }
                                                                disabled={!period1Report}
                                                            >
                                                                <OpenInNewIcon color={period1Report ? 'success' : 'disabled'} />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                            <TableCell align="center" padding="none">
                                                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                                                    <Tooltip title={period2Report ? "2.Dönem Raporu Aç" : "2.Dönem Raporu Yüklenmemiş"}>
                                                        <span>
                                                            <Button
                                                                style={{ minWidth: '30px', fontSize: iconFontSize }}
                                                                onClick={() => period2Report &&

                                                                    getOpenFileFromFullPath(period2Report.filePath)



                                                                }
                                                                disabled={!period2Report}
                                                            >
                                                                <OpenInNewIcon color={period2Report ? 'success' : 'disabled'} />
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>
                                            <TableCell align="center" padding="none">
                                                <Button variant="outlined" size="small" onClick={() => handleFileButtonClick(item)}>
                                                    Yükle/Sil
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        count={groupedData.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={(e, newPage) => setPage(newPage)}
                                        onRowsPerPageChange={(e) => setRowsPerPage(Number(e.target.value))}
                                        labelRowsPerPage="Görüntülenecek Satır"
                                        rowsPerPageOptions={[10, 50, 100, { label: "Tümü", value: -1 }]}
                                        showFirstButton
                                        showLastButton
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </Box>
                </div>

                {fileDialogStatus && (
                    <PeriodicReportDialog
                        fileList={fileList}
                        setFileList={setFileList}
                        onClose={closeFileDialog}
                        open={fileDialogStatus}
                        selectedUnit={dialogSelectedUnit}
                        updateAfterTable={updateAfterTable}
                        addAfterTable={addAfterTable}
                        existingReports={data}
                    />
                )}
            </Paper>
    );
}

export default PeriodicReportDataTable;
